import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, ImageIcon, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const MAX_MB = 10;
const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];

// Image field: upload a file to object storage OR paste a URL.
export default function ImageUpload({ value, onChange, testid = "image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    try {
      const url = await api.uploadFile(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`image-upload-${testid}`}>
      <div className="flex items-start gap-4">
        <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-sm border border-border bg-secondary grid place-items-center">
          {value ? (
            <>
              <img src={value} alt="preview" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => onChange("")}
                className="absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
                data-testid={`image-clear-${testid}`}
                aria-label="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <ImageIcon className="h-7 w-7 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 space-y-2">
          <input ref={inputRef} type="file" accept={ACCEPT.join(",")} className="hidden" onChange={handleFile} data-testid={`image-file-${testid}`} />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="gap-2" data-testid={`image-upload-btn-${testid}`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload image"}
          </Button>
          <p className="text-xs text-muted-foreground">JPG, PNG, WEBP or GIF · max {MAX_MB} MB. Or paste a URL below.</p>
          <Input
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://... or upload above"
            className="text-xs"
            data-testid={`image-url-${testid}`}
          />
        </div>
      </div>
    </div>
  );
}
